import type { Plural } from '../../core.js';

const p = (forms: Plural): Plural => forms;

/** Texts of the editor (panels, menus, plan). Part of `de`; see `../de.ts`. */
export const editor = {
  common: {
    byMeasure: 'Nach Maß anlegen',
    invalidDimensions: 'Bitte gültige Maße eingeben.',
    orthogonal: 'Nur 90°-Winkel',
    paste: 'Einfügen',
    discard: 'Verwerfen',
    duplicate: 'Duplizieren',
    show: 'Einblenden',
    hidden: 'ausgeblendet',
    outsideHalls: 'außerhalb von Hallen',
    deleteAll: 'Alle löschen',
  },
  fields: {
    stepUp: '{label} um 5 cm erhöhen',
    stepDown: '{label} um 5 cm verringern',
    stepUpShort: '5 cm mehr',
    stepDownShort: '5 cm weniger',
    width: 'Breite',
    length: 'Länge',
    depth: 'Tiefe',
    height: 'Höhe',
    eaveHeight: 'Traufhöhe',
    roomHeight: 'Raumhöhe',
    wallThickness: 'Wandstärke',
    roofType: 'Dachform',
    kind: 'Art',
    type: 'Typ',
    category: 'Kategorie',
    count: 'Anzahl',
    form: 'Form',
    label: 'Bezeichnung',
    color: 'Farbe',
    area: 'Fläche',
    note: 'Notiz',
    location: 'Lage',
    level: 'Geschoss',
    centerX: 'Mitte X',
    centerY: 'Mitte Y',
    positionX: 'Position X',
    positionY: 'Position Y',
    startX: 'Start X',
    startY: 'Start Y',
    endX: 'Ende X',
    endY: 'Ende Y',
  },
  groups: {
    dimensions: 'Maße',
    position: 'Lage & Drehung',
    metrics: 'Kennzahlen',
    roof: 'Dach',
    facade: 'Fassade',
    openings: 'Tore, Türen, Fenster',
    roofElements: 'Dachelemente',
  },
  /** Singular names of the element kinds, keyed like the document collections. */
  nouns: {
    columns: 'Stütze',
    sites: 'Grundstück',
    buildings: 'Halle',
    office: 'Bürogebäude',
    walls: 'Wand',
    openings: 'Öffnung',
    zones: 'Zone',
    rooms: 'Raum',
    objects: 'Objekt',
    measurements: 'Maß',
    roofElements: 'Dachelement',
    stairs: 'Treppe',
    roads: 'Straße',
    parkings: 'Parkplatz',
    fences: 'Zaun',
    gates: 'Schiebetor',
    mezzanines: 'Geschossfläche',
    canopies: 'Vordach',
    wallImages: 'Wandbild',
    railings: 'Geländer',
    element: 'Element',
  },
  counts: {
    sites: p({ one: '{n} Grundstück', other: '{n} Grundstücke' }),
    buildings: p({ one: '{n} Halle', other: '{n} Hallen' }),
    walls: p({ one: '{n} Wand', other: '{n} Wände' }),
    openings: p({ one: '{n} Öffnung', other: '{n} Öffnungen' }),
    zones: p({ one: '{n} Zone', other: '{n} Zonen' }),
    rooms: p({ one: '{n} Raum', other: '{n} Räume' }),
    objects: p({ one: '{n} Objekt', other: '{n} Objekte' }),
    measurements: p({ one: '{n} Maß', other: '{n} Maße' }),
    roofElements: p({ one: '{n} Dachelement', other: '{n} Dachelemente' }),
    stairs: p({ one: '{n} Treppe', other: '{n} Treppen' }),
    roads: p({ one: '{n} Straße', other: '{n} Straßen' }),
    parkings: p({ one: '{n} Parkplatz', other: '{n} Parkplätze' }),
    fences: p({ one: '{n} Zaun', other: '{n} Zäune' }),
    gates: p({ one: '{n} Schiebetor', other: '{n} Schiebetore' }),
    mezzanines: p({ one: '{n} Geschossfläche', other: '{n} Geschossflächen' }),
    canopies: p({ one: '{n} Vordach', other: '{n} Vordächer' }),
    wallImages: p({ one: '{n} Wandbild', other: '{n} Wandbilder' }),
    railings: p({ one: '{n} Geländer', other: '{n} Geländer' }),
    offices: p({ one: '{n} Bürogebäude', other: '{n} Bürogebäude' }),
    stalls: p({ one: '{n} Stellplatz', other: '{n} Stellplätze' }),
    elements: p({ one: '{n} Element', other: '{n} Elemente' }),
  },
  /** Delete buttons and menu entries per element kind. */
  remove: {
    sites: 'Grundstück löschen',
    buildings: 'Halle löschen',
    office: 'Bürogebäude löschen',
    walls: 'Wand löschen',
    openings: 'Öffnung löschen',
    closeVoid: 'Wand wieder schließen',
    zones: 'Zone löschen',
    rooms: 'Raum löschen',
    objects: 'Objekt löschen',
    measurements: 'Maß löschen',
    roofElements: 'Dachelement löschen',
    stairs: 'Treppe löschen',
    roads: 'Straße löschen',
    parkings: 'Parkplatz löschen',
    fences: 'Zaun löschen',
    gates: 'Schiebetor löschen',
    mezzanines: 'Geschossfläche löschen',
    canopies: 'Vordach löschen',
    wallImages: 'Bild löschen',
    railings: 'Geländer löschen',
    element: 'Element löschen',
    many: p({ one: '{n} Element löschen', other: '{n} Elemente löschen' }),
  },
  toolPanel: {
    aria: 'Werkzeuge',
    modes: 'Werkzeugmodus',
  },
  tools: {
    select: 'Auswahl',
    draw: 'Zeichnen',
    move: 'Verschieben',
    moveShort: 'Schieben',
    rotate: 'Drehen',
    measure: 'Messen',
  },
  steps: {
    site: '1 · Grundstück',
    hall: '2 · Halle',
    office: '3 · Bürogebäude & Treppen',
    interior: '4 · Innenaufteilung',
    openings: '5 · Tore, Türen, Fenster',
    objects: '6 · Objekte',
    outdoor: '7 · Außenanlagen',
    placed: 'Verbaute Objekte',
  },
  summary: {
    noSite: 'noch keins',
    noHall: 'noch keine',
    parcels: p({ one: '{n} Flurstück · {area}', other: '{n} Flurstücke · {area}' }),
    halls: p({ one: '{n} Halle · {area}', other: '{n} Hallen · {area}' }),
    hiddenObjects: '{n} · {hidden} ausgeblendet',
  },
  site: {
    draw: '✎ Grundstück im Plan zeichnen',
    byMeasureInfo:
      'Rechteck, Trapez oder L-Form mit festen Maßen. Die Form lässt sich danach über „Form ändern“ an den Ecken und Kanten ziehen.',
    parcels: 'Flurstücke ({n})',
    total: 'Gesamt',
    addFree: 'Weiteres Grundstück nach Maß (frei platziert)',
    addInfo:
      'Erscheint rechts neben den vorhandenen Grundstücken. Danach auswählen und an die Nachbarn heranziehen – es rastet an Ecken und Kanten ein.',
    replaceAll: 'Neu anlegen (ersetzt alle Grundstücke)',
    shapes: { rect: 'Rechteck', trapez: 'Trapez', l: 'L-Form' },
    invalidSize: 'Bitte gültige Breite und Länge eingeben.',
    cutTooLarge: 'Die Aussparung muss kleiner als das Grundstück sein.',
    invalidTop: 'Bitte eine gültige obere Breite eingeben.',
    confirmReplace: 'Alle vorhandenen Grundstücke ersetzen?',
    formAria: 'Grundstück anlegen',
    shapeAria: 'Grundstücksform',
    widthBottom: 'Breite unten',
    widthTop: 'Breite oben',
    shift: 'Versatz oben (leer = mittig)',
    cutWidth: 'Aussparung Breite',
    cutLength: 'Aussparung Länge',
    replace: 'Grundstück ersetzen',
    add: 'Grundstück hinzufügen',
    create: 'Grundstück anlegen',
  },
  sitePanel: {
    sides: { north: 'Nord', east: 'Ost', south: 'Süd', west: 'West' },
    attachTitle: 'Anfügen an „{name}“',
    attachInfoEast:
      'Rechteckiges Nachbargrundstück bündig an eine Seite. Versatz nach Osten, negativ in die Gegenrichtung. Danach lässt es sich frei verschieben und rastet an den Nachbarkanten ein.',
    attachInfoNorth:
      'Rechteckiges Nachbargrundstück bündig an eine Seite. Versatz nach Norden, negativ in die Gegenrichtung. Danach lässt es sich frei verschieben und rastet an den Nachbarkanten ein.',
    attachAria: 'Grundstück anfügen',
    side: 'Seite',
    alongLength: 'Länge an der Kante',
    offsetAlong: 'Versatz entlang der Kante',
    attach: '＋ Grundstück anfügen',
    straighten: 'Gerade ausrichten',
    straightenBy: 'Gerade ausrichten ({deg}°)',
    straightenTitle: 'Dreht so, dass die längste Grundstückskante waagerecht bzw. senkrecht liegt',
    selectAll: 'Alle Grundstücke wählen',
    rotateBackground: 'Hintergrundbild mitdrehen',
    moveBackground: 'Hintergrundbild mitverschieben',
    nameLabel: 'Bezeichnung (z. B. Flurstück)',
    perimeter: 'Umfang',
    extent: 'Gesamtabmessungen',
    allSites: 'Alle {n} Grundstücke',
    rectDims: 'Maße (Rechteck)',
    shapeInfo:
      'Ecken frei ziehen, z. B. zum Trapez. Die Pfeile an den Kanten verschieben die ganze Kante parallel. Rechtsklick auf eine Kante fügt dort einen Eckpunkt ein.',
    shapeStop: 'Form ändern beenden',
    shapeStart: 'Form ändern (Ecken und Kanten ziehen)',
    vertices: 'Eckpunkte und Kanten als Zahlen',
    positionInfo:
      'Grundstück im Plan ziehen, um es zu verschieben – es rastet an Nachbargrundstücken ein. Gerade ausrichten dreht die längste Kante waagerecht bzw. senkrecht.',
  },
  interior: {
    byMeasureInfo:
      'Erscheint in der Mitte der gewählten Halle und lässt sich danach verschieben. Frei geformte Zonen und Räume zeichnest du mit dem Werkzeug „Zeichnen“.',
    formAria: 'Zone oder Raum anlegen',
    createZone: 'Zone anlegen',
    createRoom: 'Raum erstellen',
  },
  draw: {
    title: 'Zeichnen',
    what: 'Was zeichnen',
    kinds: {
      site: 'Grundstück',
      zone: 'Zone',
      room: 'Raum',
      wall: 'Wand',
      road: 'Straße',
      parking: 'Parkplatz',
      fence: 'Zaun',
      hall: 'Halle',
      office: 'Bürogebäude',
      storey: 'Geschossumriss',
      mezzanine: 'Geschossfläche',
      railing: 'Geländer',
      column: 'Stütze',
    },
    hints: {
      column:
        'Auf die Stelle in der Halle oder im Bürogeschoss klicken, an der die Stütze stehen soll. Die Höhe ergibt sich automatisch (bis unter die Geschossfläche, die Decke oder die Traufe).',
      storey:
        'Eckpunkte des kleineren Obergeschosses auf dem Plan anklicken, am besten auf den Gebäudeecken. Abschließen mit Klick auf den ersten Punkt, Doppelklick oder Enter.',
      office:
        'Eckpunkte des Bürogebäudes nacheinander anklicken (z. B. auf dem Lageplan). Abschließen mit Klick auf den ersten Punkt, Doppelklick oder Enter. Geschosse und Höhen kommen aus dem Formular; liegt der Umriss in einer Halle, wird das Büro eingebaut.',
      railing:
        'Punkte nacheinander anklicken, z. B. entlang einer Rampe oder Empore. Abschließen mit Doppelklick, Enter oder Knopf. Das Geländer steht auf der gerade gewählten Ebene.',
      mezzanine:
        'Eckpunkte der begehbaren Geschossfläche (Bühne) innerhalb einer Halle anklicken. Abschließen mit Klick auf den ersten Punkt, Doppelklick oder Enter. Sie wird zum 1. OG der Halle.',
      hall: 'Eckpunkte der Halle nacheinander anklicken (z. B. auf dem Lageplan). Abschließen mit Klick auf den ersten Punkt, Doppelklick oder Enter. Für Flach- und Pultdach; Ecken lassen sich danach verschieben, einfügen und löschen.',
      wall: 'Im Plan von Punkt zu Punkt ziehen. Ecken und Wände werden gefangen.',
      road: 'Punkte nacheinander anklicken (z. B. auf dem Lageplan). Abschließen mit Doppelklick, Enter oder Knopf. Wer am Ende einer Straße beginnt, verlängert sie.',
      fence:
        'Punkte nacheinander anklicken. Abschließen mit Doppelklick, Enter oder Knopf. Wer am Ende eines Zauns beginnt, verlängert ihn.',
      parking:
        'Anfang und Ende der Stellplatzreihe anklicken, dann die Tiefe – auch schräg zum Raster. Oder schnell ein Rechteck aufziehen.',
      zone: 'Eckpunkte nacheinander anklicken, abschließen mit Klick auf den ersten Punkt, Doppelklick oder Enter. Oder schnell ein Rechteck aufziehen.',
      room: 'Im Plan ein Rechteck aufziehen, danach die Maße rechts korrigieren.',
      site: 'Eckpunkte nacheinander anklicken (z. B. auf dem Lageplan). Abschließen mit Klick auf den ersten Punkt, Doppelklick oder Enter. Punkte rasten an Nachbargrundstücken ein.',
    },
    wallRules: {
      title: 'Wandregeln',
      items: [
        'An der Fassade: Die Wand endet an der Innenseite der Außenwand, auch wenn du bis 50 cm darüber hinaus ziehst. Bis 15 cm Lücke wird geschlossen.',
        'An anderen Wänden: Bis 25 cm Überstand wird bündig zurückgesetzt, bis 15 cm Lücke wird geschlossen. Größere Überstände und Durchgänge bleiben.',
        'Verschieben (Maus, S, Pfeiltasten): Wände im T-Stoß oder in einer nicht exakt getroffenen Ecke werden verlängert oder verkürzt und bleiben angeschlossen (bis 5 cm Abstand gilt als verbunden). Exakte Ecken wandern als Ecke mit.',
        'Nicht angepasst werden gesperrte Wände und die Außenwände der Gebäude.',
        'Tasten: S verschieben, E Größe ändern, Alt beim Ziehen schaltet das Fangen aus.',
      ],
    },
    zoneCategory: 'Zonenkategorie',
    roomType: 'Raumart',
    wallType: 'Wandart',
    roadKind: 'Straßenart',
    parkingKind: 'Stellplatzart',
    firstPoint: 'Ersten Eckpunkt im Plan setzen',
    points: p({ one: '{n} Eckpunkt gesetzt', other: '{n} Eckpunkte gesetzt' }),
    removeLast: 'Letzten entfernen',
    finishSite: 'Grundstück abschließen',
    finishZone: 'Fläche abschließen',
    finishRoad: 'Straße abschließen',
    finishFence: 'Zaun abschließen',
    finishHall: 'Halle abschließen',
    finishOffice: 'Bürogebäude abschließen',
    finishStorey: 'Geschossumriss abschließen',
    finishMezzanine: 'Geschossfläche abschließen',
    finishRailing: 'Geländer abschließen',
    firstLinePoint: 'Startpunkt im Plan setzen',
    linePoints: p({ one: '{n} Punkt gesetzt', other: '{n} Punkte gesetzt' }),
    rowSteps: [
      'Anfang der Stellplatzreihe setzen',
      'Ende der Reihe setzen',
      'Tiefe festlegen (Seite der Stellplätze)',
    ],
  },
  mezzanine: {
    areas: 'Bereiche',
    areasInfo:
      'Die Wände auf der Geschossfläche teilen sie in Bereiche (Wanddicken und Vorsatzschalen abgezogen, Türen trennen). Ein Name ersetzt „Bereich n“ im Plan, in der Flächenübersicht und im PDF; leer lassen für die Nummer.',
    areaRow: 'Bereich {n} · {area}',
    defaultName: 'Geschossfläche {n}',
    draw: 'Geschossfläche zeichnen',
    drawInfo:
      'Begehbare Teilfläche im 1. OG der Halle (Bühne) mit Decke, Geländer, Stützen und Treppe.',
    elevation: 'Höhe OK Fußboden',
    slab: 'Deckenstärke',
    railing: 'Geländer an freien Kanten',
    columns: 'Stützen',
    color: 'Farbe',
    area: 'Fläche',
    headroom: 'Lichte Höhe darunter',
    above: 'Lichte Höhe darüber (bis Traufe)',
    addStair: 'Treppe hinzufügen',
    stairs: p({ one: '{n} Treppe', other: '{n} Treppen' }),
    showLevel: '1. OG anzeigen',
    toHall: 'Zur Halle {name}',
    info: 'Ecken im 2D-Plan ziehen; Rechtsklick auf eine Kante fügt eine Ecke ein. Auf dem 1. OG lassen sich Zonen, Räume und Objekte planen.',
  },
  wallImage: {
    section: 'Bilder & Logos',
    sectionInfo:
      'Firmenschriftzug, Logo oder Bild auf diese Wand setzen: PNG (Transparenz bleibt erhalten) oder JPEG. Es hängt ganz knapp vor der Wand, wandert mit ihr mit und lässt sich im 3D mit den Pfeilen verschieben und mit „Größe ändern“ (E) an den Ecken skalieren.',
    add: 'Bild einfügen …',
    adding: 'Lädt hoch …',
    none: 'Noch keine Bilder an dieser Wand.',
    failed: 'Das Bild konnte nicht hochgeladen werden.',
    info: 'Lage und Größe auf der Wand; transparente Stellen eines PNG zeigen die Wand dahinter.',
    offset: 'Abstand vom Wandanfang',
    bottom: 'Unterkante über Boden',
    width: 'Breite',
    height: 'Höhe',
    keepAspect: 'Seitenverhältnis beibehalten',
    flip: 'Auf die andere Wandseite',
    center: 'Mittig auf der Wand',
    toWall: 'Zur Wand',
    pixels: '{width} × {height} px',
    copy: 'Kopieren (Strg+C)',
    copied: 'Kopiert – Zielwand wählen und Strg+V',
    paste: p({ one: 'Kopiertes Bild einfügen', other: '{n} kopierte Bilder einfügen' }),
    used: 'Im Projekt verwendet',
    usedHint: 'Antippen setzt es in der zuletzt eingestellten Größe auf diese Wand.',
  },
  canopy: {
    title: 'Vordach',
    overOpening: 'Vordach über dieser Öffnung',
    overGates: 'Vordach über alle Tore dieser Wand',
    facade: 'Fassade',
    facadeInfo:
      'Wand ganz öffnen, z. B. wo zwei Hallen aneinandergebaut sind. Teile einer Fassade öffnet die Öffnungsart „Wandöffnung (offen)“; darüber bleibt die Wand stehen.',
    openWall: 'Wand ganz öffnen',
    addToWall: 'Vordach an dieser Wand',
    depth: 'Auskragung',
    height: 'Höhe Unterkante',
    pitch: 'Neigung',
    posts: 'Stützen unter der Vorderkante',
    flip: 'Auf die andere Wandseite',
    info: 'Hängt an der Wand und wandert mit ihr. Im Plan entlang der Wand ziehen; die Griffe ändern Breite und Auskragung.',
  },
  railing: {
    update: 'Geländer ändern',
    info: 'Beim Zeichnen wird erkannt, worauf das Geländer steht: Geschossfläche, Bürogeschoss oder Bürodach; sonst auf dem Gelände (z. B. an Rampen). Hallen-Flachdächer und eigene Höhen hier unter „Aufstellung“ wählen.',
    mount: 'Aufstellung',
    auto: 'Automatisch erkennen',
    ground: 'EG / Gelände',
    roofOf: 'Dach von {name}',
    custom: 'Eigene Höhe',
    elevation: 'Höhe Aufstellfläche',
  },
  ral: {
    label: 'RAL-Farbe',
    custom: 'Eigene Farbe',
    common: 'Typische Firmenfarben',
  },
  hall: {
    facadeKind: 'Fassadenart',
    facadeKinds: {
      'iso-vertical': 'Iso-Paneel senkrecht',
      'iso-horizontal': 'Iso-Paneel waagerecht',
      trapezoid: 'Trapezblech',
      smooth: 'Glatt (Putz)',
      brick: 'Klinker',
    },
    brickColors: { red: 'Klinker rot', brown: 'Klinker braun', yellow: 'Klinker gelb' },
    facadeColor: 'Fassadenfarbe',
    trimColor: 'Profile und Dachrand',
    gateColor: 'Torfarbe',
    roofColor: 'Dachfarbe',
    storeyColor: 'Fassade {level}',
    likeBuilding: 'Wie Gebäude',
    standardRoof: 'Standard',
    floorKind: 'Hallenboden',
    floorKinds: {
      concrete: 'Betonboden (geglättet)',
      coated: 'Industrieboden beschichtet',
      plain: 'Einfarbig',
    },
    facadeInfo:
      'Darstellung in 3D, im Screenshot und in den 3D-Ansichten des PDF. Iso-Paneele mit 1,00 m Raster; die Profilfarbe gilt für Eckprofile, Dachrand, Rinne und die Rahmen von Toren, Türen und Fenstern.',
    create: 'Halle erstellen',
    invalid: 'Bitte gültige Hallenmaße eingeben.',
    roofTypes: { gable: 'Satteldach', mono: 'Pultdach', flat: 'Flachdach' },
    pitch: 'Dachneigung',
    areaLine: 'Bebaute Fläche {built} · Nutzfläche innen {net}',
    areaLineOffice: 'Bebaute Fläche {built}',
    areaLineFloors: 'Bebaute Fläche {built} · BGF {gross} ({n} Geschosse)',
    pitchInfo: 'entspricht {percent} % Gefälle · zulässig {min}–{max}°',
    drawInPlan: 'Halle im Plan zeichnen',
    drawRoofOnly: 'Gezeichnete Hallen haben ein Flach- oder Pultdach.',
    eaveEdge: 'Traufkante (tiefe Seite)',
    edgeOption: 'Kante {n} · {length} · {dir}',
    directions: ['O', 'NO', 'N', 'NW', 'W', 'SW', 'S', 'SO'],
    outlineInfo:
      'Gezeichneter Grundriss mit {n} Ecken. Ecken im 2D-Plan ziehen; Rechtsklick auf eine Kante fügt eine Ecke ein, Rechtsklick auf eine Ecke löscht sie.',
    grossArea: 'Hallenfläche (außen)',
    netArea: 'Nutzfläche innen',
    ridgeHeight: 'Firsthöhe',
    openingsInfo: 'Wand wählen, um Öffnungen einzeln oder als Reihe einzusetzen.',
    roofElementsInfo:
      'Lichtkuppeln, Lichtbänder und Rauch- und Wärmeabzüge (RWA), einzeln oder als Raster. Gesetzte Elemente im Grundriss antippen, um sie zu verschieben oder zu ändern.',
  },
  structure: {
    title: 'Tragwerk',
    info: 'Gevoutete Stahlrahmen aus Stützen und Bindern (I-Profile), gleichmäßig im Rastermaß verteilt; die Stützen stehen innen an der Fassade, bei senkrechten Paneelen hinter den Wandriegeln. Bei mehr als 30 m Hallenbreite kommt eine Reihe Mittelstützen dazu, standardmäßig unter jedem zweiten Binder; die Binder dazwischen liegen auf einem Unterzug. Ein Raster über 5 m macht die Rahmen automatisch stärker, ab 7,5 m tragen Walzprofile statt Kaltprofilen als Pfetten und Wandriegel. Die Profile sind eine Vorbemessung zur Orientierung (Schneelast aus den Projekteinstellungen unter „Lasten“) und ersetzen keine Statik.',
    enabled: 'Tragwerk berechnen und darstellen',
    system: 'Bauweise',
    systems: {
      steel: 'Stahlrahmen',
      hybrid: 'Stahlbetonstützen mit Stahlbindern',
      precast: 'Betonfertigteile (Stützen und Spannbetonbinder)',
    },
    systemsInfo:
      'Stahlbetonstützen stehen eingespannt in Köcherfundamenten (Querschnitt etwa Höhe/18, mindestens 40 cm) und steifen die Wände aus. Binderabstand standardmäßig 7,50 m; Mittelstützen erst ab 30 m (Stahlbinder) bzw. 40 m Spannweite (Spannbeton). Stahlbinder liegen gelenkig auf; Spannbetonbinder folgen oben der Dachneigung, sind höchstens etwa Spannweite/15 hoch und bei steilerem Dach steigt ihre Unterseite mit an.',
    concreteColumn: 'Stützenquerschnitt (Beton)',
    girderProfile: 'Binderprofil (Einfeldträger)',
    girder: 'Binderhöhe (Auflager / größte)',
    girderValue: '{support} / {deepest}',
    roofBracing: 'Dachverband und Druckriegel',
    bay: 'Rastermaß (max.)',
    interiorRows: 'Mittelstützen',
    rows: ['Keine', '1 Reihe', '2 Reihen', '3 Reihen'],
    interiorSpacing: 'Mittelstützen an',
    spacings: ['Jeder Achse', 'Jeder 2. Achse', 'Jeder 3. Achse'],
    beamProfile: 'Unterzug (Mittelreihe)',
    auto: '{value} (auto)',
    columnProfile: 'Stützenprofil',
    rafterProfile: 'Binderprofil',
    gableColumns: 'Giebelwandstützen',
    purlins: 'Pfetten (Dach)',
    girts: 'Wandriegel (für senkrechte Paneele)',
    girtsOptional: 'Wandriegel (bei dieser Fassade nicht nötig)',
    bracing: 'Verbände und Druckriegel',
    haunches: 'Vouten an den Rahmenecken',
    conflict: '{opening} trifft eine Stütze in Achse {axis}',
    conflictGable: '{opening} trifft eine Giebelwandstütze',
    grid: 'Raster',
    gridValue: '{n} Felder × {length}',
    rafterSpan: 'Spannweite Binder',
    roofLoad: 'Dachlast (Schnee + Eigengewicht)',
    roofLoadValue: '{value} kN/m²',
    windLoad: 'Winddruck qₚ (Firsthöhe)',
    clearWidth: 'Lichte Breite zwischen Stützen',
    clearLength: 'Lichte Länge',
    outline: 'Für gezeichnete Hallenumrisse wird das Tragwerk noch nicht dargestellt.',
  },
  pv: {
    label: 'PV-Anlage auf dem Dach (+{load} kN/m²)',
    share: 'Belegte Dachfläche',
    info: 'Module mit Unterkonstruktion als zusätzliche ständige Dachlast (etwa 20 kg/m²) für Binder, Pfetten, Stützen und Fundamente; in der Kalkulation als kWp aus der belegten Dachfläche (etwa 0,2 kWp je m²).',
  },
  officeStructure: {
    title: 'Tragwerk',
    info: 'Tragende Wände: die Decken liegen auf Außenwänden und massiven Innenwänden; geprüft wird, ob die Decke die Spannweite schafft. Stahlbetonskelett: Stützen im Raster (Fassade gleichmäßig geteilt), bemessen aus der Fläche, die jede Stütze trägt, und allen Geschossen darüber (Büro 3 kN/m², nutzbare Dachdecke 5 kN/m², Dach mit Schnee); die Decke als Flachdecke oder auf Unterzügen. Geschlossene Treppenhäuser zählen als Stahlbetonkern. Vorbemessung zur Orientierung, ersetzt keine Statik.',
    system: 'Tragsystem',
    systems: {
      walls: 'Tragende Wände',
      flat: 'Stahlbetonskelett mit Flachdecken',
      beams: 'Stahlbetonskelett mit Unterzügen',
    },
    grid: 'Stützenraster (max.)',
    columnSize: 'Stützenquerschnitt',
    auto: '{value} (auto)',
    fields: 'Raster',
    fieldsValue: '{nx} × {ny} Felder, {x} × {y}',
    columnsOf: 'Stützen {level}',
    columnsValue: '{n} Stück, {size} × {size}',
    beam: 'Unterzüge (b × h)',
    beamValue: '{width} × {height}',
    span: 'Spannweite Decke',
    slab: 'Deckendicke',
    slabValue: '{current} (empfohlen {recommended})',
    adopt: 'Empfohlene Deckendicke {value} übernehmen',
  },
  costs: {
    title: 'Kalkulation',
    intro:
      'Mengen aus dem Plan mal Einheitspreis dieses Projekts. Preise und Zuschläge lassen sich direkt in der Tabelle ändern.',
    rangeLabel: 'Kostenrahmen:',
    steelExtra: 'Zuschlag Anschlüsse (Stahl)',
    range: 'Spanne ±',
    morePrices: 'Weitere Einheitspreise',
    resetPrices: 'Alle eigenen Preise entfernen (Katalogpreise verwenden)',
    catalog: 'Preiskatalog',
    catalogDefault: 'Standard ({name})',
    ownPriceReset: 'Eigener Preis – zurück auf den Katalogpreis',
    foundationsFromColumns: 'Fundamente aus Stützen und Wänden ermitteln',
    foundationsFromColumnsHint:
      'Einzelfundamente aus der Last jeder Stütze (Bodenpressung 250 kN/m², außen frostfrei 80 cm tief), Streifenfundamente und Frostschürzen unter den Wänden; ersetzt den Prozent-Zuschlag für Fundamente.',
    foundationsMeasured: 'ermittelt',
  },
  hallSections: {
    title: 'Bereiche durch Trennwände',
    info: 'Automatisch aus den Trennwänden ermittelt, gemessen bis Wandachse. Lücken bis 1 m zur nächsten Wand gelten als geschlossen (gestrichelt im Plan).',
    adoptedAs: 'Übernommen als',
    hasHoles: 'Enthält ein Büro oder einen Raum – Zone bitte von Hand zeichnen.',
    zoneKindFor: 'Zonenart für {name}',
    adopt: 'Als Zone übernehmen',
  },
  roofElements: {
    daylight: 'Tageslichtanteil Dach',
    smokeVentArea: 'RWA-Fläche (geometrisch)',
    typeAria: 'Dachelement',
    arrangement: 'Anordnung',
    grid: 'Raster',
    single: 'Einzeln',
    gableAria: 'Lage auf dem Satteldach',
    halves: 'Je Dachhälfte',
    ridge: 'Auf dem First',
    roof: 'Ganze Fläche',
    widthAcross: 'Breite (quer)',
    lengthAlong: 'Länge (längs)',
    spacingU: 'Achsabstand quer',
    spacingV: 'Achsabstand längs',
    marginHalves: 'Abstand Traufe/First/Giebel',
    margin: 'Randabstand',
    ridgeHint: '„quer“ = quer zum First, „längs“ = entlang des Firsts.',
    halvesHint: 'Symmetrisch zum First, mittig auf beiden Dachflächen.',
    eaveHint: '„quer“ = quer zur Traufe, „längs“ = entlang der Traufe.',
    outlineHint: 'Gesetzt werden nur Elemente, die samt Randabstand ganz auf dem Dach liegen.',
    placeSingle: '{type} in Hallenmitte setzen',
    placeSingleRoof: '{type} aufs Dach setzen',
    placeGrid: '{n} × {type} setzen',
    noFit: 'Passt mit diesen Abständen nicht aufs Dach',
    removeAll: 'Alle Dachelemente dieser Halle entfernen',
  },
  office: {
    title: 'Bürogebäude',
    infoIndoor:
      'In der Halle: steht unter dem Hallendach, oben eine Betondecke, als Fläche nutzbar (z. B. Lagerbühne). Geschosse und Höhen lassen sich danach je Geschoss anpassen.',
    infoOutdoor:
      'Freistehend mit Flachdach, z. B. vor der Halle. Geschosse und Höhen lassen sich danach je Geschoss anpassen.',
    formAria: 'Bürogebäude anlegen',
    placementAria: 'Lage des Bürogebäudes',
    indoor: 'In der Halle',
    outdoor: 'Freistehend',
    floors: 'Geschosse',
    clearHeight: 'Lichte Höhe je Geschoss',
    slab: 'Decke inkl. Fußboden',
    invalid: 'Bitte gültige Maße eingeben (mind. 2 m, lichte Höhe mind. 2 m).',
    create: 'Bürogebäude anlegen',
    drawOutline: 'Umriss zeichnen (z. B. Trapez)',
  },
  officePanel: {
    areasInfo:
      'Selbst gezeichnete Wände teilen das angezeigte Geschoss in Bereiche (Wanddicken und Vorsatzschalen abgezogen, Türen trennen, angelegte Räume zählen für sich). Ein Name ersetzt „Bereich n“ im Plan und in der Flächenübersicht; ⌖ zoomt hin.',
    enterMode: 'Bürogebäude bearbeiten (Büromodus)',
    placementIndoor: 'In der Halle (Einbau)',
    placementOutdoor: 'Außen (freistehend, Flachdach)',
    dimsFloors: 'Maße & Geschosse',
    floorCount: 'Anzahl Geschosse',
    clearHeightOf: 'Lichte Höhe {level}',
    usableTop: 'Decke als Empore nutzen (Geländer, per Treppe erreichbar)',
    deckRailing: 'Brüstung der Empore',
    sideFront: 'Vorne',
    sideRight: 'Rechts',
    sideBack: 'Hinten',
    sideLeft: 'Links',
    sideLength: '{side} ({length})',
    railingAll: 'Alle',
    railingLong: 'Nur lange Seiten',
    railingNone: 'Keine',
    railingAutoHint:
      'Entlang einer Hallenwand oder einer angrenzenden Bühne entfällt die Brüstung automatisch.',
    deckStair: 'Treppe auf die Empore',
    storeys: 'Geschosse (Staffelgeschoss)',
    storeysInfo:
      'Ein Obergeschoss kann kleiner sein als das darunter: Umriss im Plan auf dem Geschoss nachzeichnen. Die Außenwände folgen dem neuen Umriss, auf der freien Fläche daneben liegt ein Flachdach mit Attika oder eine Dachterrasse mit Geländer. Geschosse darüber übernehmen den Umriss.',
    storeyInherited: '{level}: wie darunter',
    storeyOwn: '{level}: eigener Umriss · {area}',
    storeyDraw: 'Umriss zeichnen',
    storeyReset: 'Wie darunter',
    storeyFree: 'Freie Fläche daneben',
    storeyFreeRoof: 'Flachdach mit Attika',
    storeyFreeTerrace: 'Dachterrasse mit Geländer',
    removedPieces: 'Entfernte Außenwände',
    removedPiecesInfo:
      'Eine Außenwand im 3D oder Plan anklicken und löschen entfernt sie nur in diesem Geschoss. In Obergeschossen steht dort ein Geländer, wenn gewünscht.',
    removedPiece: '{level} · Wand {n}',
    restorePiece: 'Wiederherstellen',
    showDeck: 'Empore anzeigen und bearbeiten',
    restoreWalls: 'Außenwände wiederherstellen',
    extendRooms: 'Räume an die Deckenkante anpassen',
    removedWalls:
      'Entfernte Außenwände: {n}. Sie bleiben weg, auch beim Verschieben oder Vergrößern.',
    totalHeight: 'Gesamthöhe',
    floorOf: 'Fußboden {level}',
    airTo: 'Luft bis Traufe {name}',
    footprint: 'Grundfläche',
    gfa: 'BGF alle Geschosse',
    stairs: 'Treppen',
    noStairs: 'Noch keine Treppe – links unter „3 · Bürogebäude & Treppen“ einfügen.',
    walls: 'Außenwände · Türen, Fenster',
    upperWindows: 'Fenster im OG: Öffnung wählen und im Feld „Geschoss“ das Stockwerk einstellen.',
    unassigned: 'Nicht zugeordnet · {area} ({w} × {l} m)',
    restRoomType: 'Raumart für Restfläche',
    adoptRoom: 'Als Raum übernehmen',
    roomBook: 'Räume · Raumbuch',
    noRooms:
      'Noch keine Räume. Räume im Plan mit „Zeichnen → Raum“ aufziehen oder unten eine Vorlage nutzen.',
    unassignedOf: 'davon nicht zugeordnet {area}',
    templateFor: 'Raumaufteilung aus Vorlage für {level}',
    template: 'Vorlage',
    templateAria: 'Raumvorlage',
    templateWallAria: 'Wandart der Vorlage',
    templateHint: 'Das Geschoss wird im Plan oben rechts gewählt.',
    applyTemplate: 'Vorlage auf {level} anwenden',
  },
  stair: {
    title: 'Treppe',
    info: 'Innen sitzt die Treppe in der Nordost-Ecke des Bürogebäudes, außen angebaut an der Ostwand. Danach frei verschiebbar und drehbar.',
    insert: 'Treppe einfügen',
    building: 'Gebäude',
    buildingAria: 'Gebäude der Treppe',
    needsFloor:
      'Für eine Treppe braucht das Gebäude ein zweites Geschoss oder eine nutzbare Decke.',
    formAria: 'Treppenform',
    upTo: 'Bis Geschoss',
    from: 'Von Geschoss',
    stairwellHint:
      'Je Geschoss eine Treppe übereinander; Verschieben, Ändern und Löschen gelten für alle.',
    fromAria: 'Treppe von Geschoss',
    width: 'Laufbreite',
    outside: 'Außen am Gebäude (angebaut)',
    podest: 'Tiefe des Austrittspodests',
    landingOn: 'Austrittspodest oben (Auslauffläche)',
    construction: 'Ausführung',
    constructionSolid: 'Massiv (geschlossener Körper)',
    constructionSteel: 'Stahltreppe (offen, darunter frei)',
    constructionSlab: 'Betontreppe (Laufplatte, darunter frei)',
    goingOwn: 'Auftritt (cm)',
    riserCount: 'Steigungen',
    standard: 'Standard',
    entry: 'Vorplatz vor der ersten Stufe',
    restoreStairwell: 'Treppenhauswände wiederherstellen',
    protrudes:
      'Das Treppenhaus ragt über das Gebäude hinaus. Auftritt, Steigungen oder Vorplatz verkleinern oder die Treppe verschieben.',
    withDoor: 'Tür vom Podest ins Obergeschoss einsetzen',
    enclosed: 'Treppenhaus mit Wänden',
    midLanding: 'Zwischenpodest (Lauf in der Mitte geteilt, 1 m)',
    sideExit: 'Austritt seitlich',
    sideExitNone: 'Keiner (geradeaus)',
    sideExitLeft: 'Nach links',
    sideExitRight: 'Nach rechts',
    sideExitLength: 'Länge der Plattform',
    sideExitToWall: 'Bis zur Wand verlängern',
    sideExitNoWall: 'In dieser Richtung ist innerhalb von 20 m keine Wand auf Plattformhöhe.',
    sideExitHint:
      'Plattform oben in Treppenbreite, z. B. über Stützen und Riegel der Hallenwand hinweg zur Tür ins Büro.',
    snapping: 'Einrasten und Andocken',
    snappingHint:
      'Aus: Die Treppe lässt sich frei verschieben, ohne Raster und ohne an Wände oder Stützen zu springen. Am PC geht das auch mit gedrückter Alt-Taste.',
    startOn: 'Antritt auf',
    leadsTo: 'Führt auf',
    roofOf: 'Dach {name}',
    hallFloor: 'Hallenboden (EG)',
    startHeight: 'Antrittshöhe',
    ownHeight: 'Eigene Höhe',
    wallSides: 'Wände des Treppenhauses',
    sides: { front: 'Antritt (mit Tür)', back: 'Hinten (Podest)', left: 'Links', right: 'Rechts' },
    risers: 'Steigungen',
    going: 'Auftritt',
    footprint: 'Grundfläche',
    turn: 'Wendung',
    turnRight: 'nach rechts',
    turnLeft: 'nach links',
    addDoor: 'Tür am Podest einsetzen',
    floorHeight: 'Geschosshöhe',
    ownRise: 'Treppenhöhe (eigene)',
    autoRise: 'Höhe wieder automatisch',
    stepRule: 'Schrittmaß (2 s + a)',
    stairFootprint: 'Grundfläche Treppe',
    asrOk:
      'Entspricht den Richtwerten der ASR A1.8 (Planungshinweis, keine Prüfung nach Bauordnung).',
    toBuilding: 'Zum Gebäude ({name})',
  },
  fence: {
    kind: 'Zaunart',
    color: 'Farbe',
    colors: {
      ral6005: 'Moosgrün (RAL 6005)',
      ral7016: 'Anthrazit (RAL 7016)',
      galvanized: 'Verzinkt',
    },
    customColor: 'Eigene Farbe',
    addGate: 'Schiebetor einfügen',
    addGateHere: 'Schiebetor in diesen Zaun einfügen',
    gateInfo:
      'Elektrisches, freitragendes Schiebetor. Frei verschiebbar; in der Nähe eines Zauns rastet es auf der Zaunlinie ein und spart den Zaun dort aus.',
    gateWidth: 'Torbreite',
    opensTo: 'Öffnet nach',
    left: 'links',
    right: 'rechts',
    addDoor: 'Durchgangstür einfügen',
    addDoorHere: 'Durchgangstür in diesen Zaun einfügen',
    doorTitle: 'Durchgangstür',
    removeDoor: 'Durchgangstür löschen',
    doorInfo:
      'Einflügelige Tür für Fußgänger. Frei verschiebbar; in der Nähe eines Zauns rastet sie auf der Zaunlinie ein und spart den Zaun dort aus. Drehen um 180° wechselt die Öffnungsrichtung.',
    doorWidth: 'Durchgangsbreite',
    hinge: 'Anschlag',
    gateKind: 'Art',
    kinds: { sliding: 'Schiebetor', door: 'Durchgangstür' },
  },
  column: {
    title: 'Stütze',
    place: 'Stütze setzen (Stahl oder Beton)',
    material: 'Ausführung',
    materials: { steel: 'Stahlstütze (HEB)', concrete: 'Betonstütze' },
    height: 'Höhe',
    auto: 'automatisch',
    autoHeight: 'Automatische Höhe ({height})',
    standsOn: 'Steht in',
    hint: 'Ohne eigene Höhe reicht die Stütze in der Halle bis unter die Geschossfläche darüber oder bis zur Traufe, im Bürogebäude bis unter die Decke des Geschosses. Eine eigene Höhe zählt ab dem Boden, auf dem sie steht.',
    remove: 'Stütze löschen',
    toBuilding: 'Zu „{name}“',
    mezzanineColumns: 'Stützen',
    detach: 'Stützen einzeln bearbeiten',
    detachHint:
      'Macht aus den automatischen Stützen einzelne Stahlstützen, die sich löschen, verschieben und ändern lassen.',
    restore: 'Automatische Stützen wiederherstellen',
    detached: '{n} einzelne Stützen',
  },
  lining: {
    title: 'Vorsatzschale (Gipskarton)',
    info: 'Gipskarton-Vorsatzschale vor der Wand, z. B. vor der nackten Hallenwand im Bürobereich auf der Geschossfläche: vom Boden des gewählten Geschosses bis zur Decke, mit Fensterbänken an den Fenstern; Türen und Tore bleiben frei. Sie verkleinert die Raumflächen.',
    add: 'Vorsatzschale anbringen',
    sideRight: 'Rechte Wandseite',
    sideLeft: 'Linke Wandseite',
    level: 'Geschoss',
    from: 'Von (ab Wandanfang)',
    to: 'Bis (ab Wandanfang)',
    thickness: 'Dicke',
    span: 'Höhe',
    spanValue: '+{base} bis +{top}',
    color: 'Farbe',
    remove: 'Vorsatzschale entfernen',
  },
  handover: {
    title: 'Übergabetore',
    info: 'Schiebegitter im Geländer, z. B. um mit dem Stapler Paletten auf die Geschossfläche zu stellen. Das Geländer endet beidseits am Tor; beim Begehen schiebt es sich zur Seite, bleibt aber Absturzsicherung.',
    add: 'Übergabetor einfügen',
    edge: 'Kante',
    edgeOption: 'Kante {n} ({length})',
    offset: 'Abstand vom Kantenanfang',
    width: 'Breite',
    remove: 'Übergabetor entfernen',
  },
  wall: {
    interior: 'Innenwand',
    exterior: 'Außenwand',
    throughMezzanine: 'Bis zum Hallenboden durchgehend',
    throughMezzanineHint:
      'Aus: Die Wand steht auf der Geschossfläche. An: Sie geht durch bis auf den Hallenboden; die Oberkante bleibt.',
    cladding: 'Oberfläche',
    claddings: {
      plain: 'Glatt / verputzt',
      sandwich: 'Iso-Paneel senkrecht',
      'sandwich-horizontal': 'Iso-Paneel waagerecht',
      'sand-lime': 'Kalksandstein',
      concrete: 'Sichtbeton',
      brick: 'Klinker',
    },
    wallType: 'Wandart',
    ownWallType: 'Eigene (Dicke/Oberfläche angepasst)',
    direction: 'Richtung',
    ownerHall: 'Diese Wand wird über die Maße von Halle „{name}“ gesteuert.',
    removeExterior: 'Außenwand entfernen',
    floorRailing: 'Geländer',
    removeOnFloor: 'Nur im {level} entfernen',
    ownerRoom: 'Diese Wand wird über die Maße von Raum „{name}“ gesteuert.',
    toHall: 'Zu Halle „{name}“',
    toRoom: 'Zu Raum „{name}“',
    ownerStair: 'Diese Wand gehört zum Treppenhaus „{name}“ und wandert mit der Treppe.',
    toStair: 'Zur Treppe „{name}“',
    removeStairWall: 'Treppenhauswand entfernen',
    openingsInfo:
      'Eine Öffnung landet in der Wandmitte oder neben vorhandenen. Mehrere werden als Reihe mit dem angegebenen lichten Abstand eingesetzt.',
    noOpenings: 'Noch keine Öffnungen in dieser Wand.',
    openingType: 'Öffnungstyp',
    gap: 'Abstand',
    gapAria: 'Abstand zwischen den Öffnungen',
    insert: 'Einsetzen',
    sill: 'Brüstung',
    spread: 'Verteilung',
    spreadGap: 'Fester Abstand, mittig',
    spreadEven: 'Gleichmäßig über die Wand',
    storeys: 'In Geschossen',
    storeysAria: 'Geschosse, in die die Öffnungen gesetzt werden',
    tooMany: '{n} × {width} passen nicht auf die Wand ({length}).',
    noStorey: 'Bitte mindestens ein Geschoss wählen.',
    floor: 'Geschoss',
    floorGround: 'Boden (EG)',
    floorTooHigh: 'Die Öffnung passt auf diesem Geschoss nicht mehr in die Wandhöhe.',
    insertAtClick: 'Position: {offset} vom Wandanfang (Klickstelle)',
    insertCentered: 'Position: mittig – auf die Wand klicken, um die Stelle zu wählen',
  },
  opening: {
    levelAria: 'Geschoss der Öffnung',
    dimsPosition: 'Maße & Lage',
    dimsInfo:
      'Im Grundriss oder in 3D lässt sich die Öffnung entlang der Wand ziehen, auch an anderen Öffnungen vorbei; losgelassen auf einer anderen springt sie an die nächste freie Stelle.',
    sill: 'Brüstung (über Geschossboden)',
    head: 'Oberkante (über Geschossboden)',
    heights: 'Brüstung {sill} · Oberkante {head}',
    heightsOver: 'Brüstung {sill} · Oberkante {head} (über {floor})',
    overFloor: 'Gemessen ab dem Boden von {floor}.',
    swing: 'Öffnungsrichtung',
    swingInfo:
      'Wohin das Türblatt aufschlägt; der Bogen im Grundriss und das Aufgehen beim Begehen folgen der Einstellung.',
    flipSide: 'Zur anderen Wandseite',
    flipHinge: 'Anschlag wechseln',
    slideTo: 'Schieberichtung',
    slideInfo:
      'Das Torblatt hängt an einer Laufschiene über der Öffnung und schiebt beim Begehen zur Seite über die Wand. Links und rechts gelten von außen gesehen.',
    slideLeft: '← nach links',
    slideRight: 'nach rechts →',
    otherFace: 'Wandseite wechseln',
    mountSide: 'Einbauseite',
    mountInfo:
      'Haube mit Wickelwelle und Führungsschienen sitzen auf einer Wandseite; das Torblatt ist eine graue Plane mit Sichtfenster (1,40–2,00 m), die sich beim Begehen schnell nach oben aufrollt. Innen ist bei Hallen die Hallenseite, sonst die linke Wandseite vom Wandanfang gesehen.',
    mountInside: 'Innen',
    mountOutside: 'Außen',
    slideNoRoom:
      'Auf dieser Seite ist zu wenig Wand für das offene Torblatt (nötig: {length}). Schieberichtung wechseln oder das Tor verschieben.',
    colors: 'Farben',
    colorsInfo:
      'Eigene Farben dieser Öffnung; ohne eigene Farbe gelten Profil- und Torfarbe des Gebäudes. Bei mehreren gewählten Öffnungen gilt die Farbe für alle.',
    frameColor: 'Rahmenfarbe',
    leafColor: 'Blattfarbe (Tür- bzw. Torblatt)',
    likeBuilding: 'Wie Gebäude',
    voidPosts: 'Stützen an den Rändern (höchstens 6 m Abstand)',
    offsetStart: 'Abstand Wandanfang',
    offsetEnd: 'Abstand Wandende',
    toWall: 'Zur Wand ({length})',
    leveler: 'Überladebrücke (in der Halle)',
    shared:
      'Dieses Tor teilt sich die abgesenkte Zufahrt mit seinen Nachbartoren; sie wird beim ersten Tor der Reihe eingestellt.',
    toLead: 'Zur gemeinsamen Zufahrt',
    gates: 'Rampen in dieser Zufahrt',
    addGate: 'Rampe hinzufügen',
    removeGate: 'Rampe entfernen',
    railingLeft: 'Geländer links',
    railingRight: 'Geländer rechts',
    railingInfo:
      'Links und rechts vom Hof aus auf die Tore gesehen. Neben einer Hallenwand kann das Geländer entfallen.',
    approach: 'Andockzufahrt (abgesenkt)',
    approachInfo:
      'Empfehlung: 16 m ebene Standfläche, damit der Auflieger waagerecht steht, danach eine Rampe mit höchstens 10 % Gefälle. Das Gefälle bestimmt die Rampenlänge.',
    pitDepth: 'Vertiefung',
    bayWidth: 'Breite Zufahrt',
    bayLength: 'Standfläche (Länge)',
    rampLength: 'Rampe (Länge)',
    slope: 'Gefälle Rampe (%)',
    slopeAria: 'Gefälle Rampe',
    approachOffset: 'Versatz Zufahrt (entlang Wand)',
    incline: 'Neigung',
    vertical: '– (senkrechter Absatz)',
    totalLength: 'Gesamtlänge außen',
  },
  sweep: {
    title: 'Schleppkurve',
    info: 'Bemessungsfahrzeug nach RASt 06 / EAR. Farbig: Fläche, die das Fahrzeug bei genauer Fahrt auf der Leitlinie überstreicht; gestrichelt: 0,5 m Sicherheitsabstand (Fahrungenauigkeit, Spiegel); Linien: Spur des äußeren Vorderrads und des inneren Hinterrads. Dunkel: Führerhaus, Pfeile: Fahrtrichtung. Mit „Rückwärts anhängen“ entsteht ein Rangiermanöver, z. B. vorwärts an der Rampe vorbei und dann rückwärts hinein. Rot markiert: Fahrfläche außerhalb des Grundstücks oder im Gebäude, orange: nur der Sicherheitsabstand. Die Kurve blockiert nichts und lässt sich wie ein Objekt verschieben, drehen, sperren und ausblenden; ihr Startpunkt bleibt beim Ändern liegen.',
    angleLess: '{step}° weniger',
    angleMore: '{step}° mehr',
    move: 'Fahrt {n}',
    removeMove: 'Entfernen',
    addReverse: '+ Rückwärts anhängen',
    addForward: '+ Vorwärts anhängen',
    addMoveHint: 'Weitere Fahrt, die dort beginnt, wo das Fahrzeug stehen bleibt (Rangieren).',
    conflictSite: 'Fahrfläche verlässt das Grundstück.',
    conflictSiteMargin: 'Sicherheitsabstand reicht über die Grundstücksgrenze.',
    conflictBuilding: 'Fahrfläche ragt in {name}.',
    conflictBuildingMargin: 'Sicherheitsabstand reicht an {name} heran.',
    direction: 'Fahrtrichtung',
    forward: 'Vorwärts',
    reverse: 'Rückwärts (an die Rampe)',
    side: 'Abbiegen',
    left: 'Links',
    right: 'Rechts',
    angle: 'Abbiegewinkel',
    uTurn: '180° (Wende)',
    angleFree: 'Winkel frei (°)',
    radius: 'Radius (ab {min})',
    lead: 'Gerade davor',
    tail: 'Gerade danach',
    leadReverse: 'Gerade zurück vor der Kurve',
    tailReverse: 'Gerade zurück bis zur Rampe',
    vehicle: 'Fahrzeug',
    vehicleSize: '{length} × {width}',
    space: 'Platzbedarf (mit Abstand)',
  },
  object: {
    model: '3D-Modell',
    hidden: 'Dieses Objekt ist ausgeblendet.',
    levelAria: 'Geschoss des Objekts',
    heightAboveFloor: 'Höhe über Geschossboden',
    heightAboveGround: 'Höhe über Boden',
    snapping: 'Einrasten und Andocken',
    snappingHint:
      'Aus: Das Objekt lässt sich frei verschieben, ohne Raster und ohne an Wände oder Objekte zu springen. Am PC geht das für jedes Objekt auch mit gedrückter Alt-Taste.',
    size: 'Abmessungen',
    proportional: 'Proportional skalieren',
    rackPlaces: 'Palettenplätze',
    rackLayout: p({
      one: '{n} Feld × {levels} Ebenen × {perBay} Paletten',
      other: '{n} Felder × {levels} Ebenen × {perBay} Paletten',
    }),
    rackPlacesLow: '{places} (davon {low} für 0,80 m)',
    rackLevels: 'Fachhöhen (lichte Höhe, von unten)',
    rackCompartment: 'Fach {n} · {load}',
    rackTop: 'Fach {n} oben · {load}',
    rackTopClear: '{clear} bis Oberkante Rahmen',
    rackLoadHigh: 'Palette 1,60 m',
    rackLoadLow: 'Palette 0,80 m',
    rackLoadNone: 'leer',
    rackResetLevels: 'Standard-Fachhöhen (1,75 m)',
    rackPassages: 'Durchgänge',
    rackPassageInfo:
      'Ein Feld als Laufdurchgang: Bis zur Durchgangshöhe stehen dort keine Traversen und Paletten, darüber geht das Regal weiter. Unter der ersten Traverse liegt ein Durchgangsschutz (Schutzgitter). Beim Begehen kann man hindurchgehen.',
    rackPassageBay: 'Feld {n} als Durchgang',
    rackPassageHeight: 'Durchgangshöhe',
    rackPassageFree: 'Frei (Breite × Höhe)',
    rackPassageNarrow:
      'Das Feld ist nur {width} breit – für einen Durchgang von 2,00 m ein Regal mit längeren Traversen wählen.',
    shelfPlaces: 'Stellplätze (Karton 30 × 60 cm)',
    shelfArea: 'Fachbodenfläche',
    shelfLayout: p({
      one: '{n} Feld × {levels} Fächer · {places} Stellplätze',
      other: '{n} Felder × {levels} Fächer · {places} Stellplätze',
    }),
    shelfCartons: '3 Kartons',
    shelfResetLevels: 'Fachböden gleichmäßig verteilen',
    shelfHint:
      'Die Länge rastet auf ganze Felder ein (1,05 m, jedes weitere Feld 1,00 m), die Höhe in 50-cm-Schritten. Ein Fach zählt, wenn ein Karton 30 × 60 × 28 cm hineinpasst; die übrige Höhe wird gleichmäßig auf weitere Fächer verteilt.',
    rackHint:
      'Die Länge rastet auf ganze Felder ein; benachbarte Felder teilen sich einen Stützrahmen. Ab 1,60 m freier Höhe wird eine 1,60-m-Palette eingelagert, ab 0,80 m eine 0,80-m-Palette, sonst bleibt das Fach leer.',
    clearance: 'Freiraum',
    clearanceInfo:
      'Bedien- und Wartungsfreiraum um das Objekt; wird bei der Kollisionsprüfung berücksichtigt. Planungshinweis, keine Sicherheitsbewertung.',
    clearanceToggle: 'Bedien-/Wartungsfreiraum',
    front: 'Vorne',
    back: 'Hinten',
    left: 'Links',
    right: 'Rechts',
    nativeSize: 'Originalgröße',
    scale: 'Maßstab',
    loadError: 'Modell konnte nicht geladen werden – es wird ein Platzhalter angezeigt.',
    restoreSize: 'Originalgröße wiederherstellen',
  },
  multi: {
    title: 'Temporäre Gruppe',
    summary:
      '{items} ausgewählt. Ziehen verschiebt alle gemeinsam; im Schieben-Modus gibt es dafür einen gemeinsamen Griff.',
    deselect: 'Auswahl aufheben',
    deselectHint: 'Auch mit Esc oder per Rechtsklick ins Leere.',
    rotateTogether: 'Gemeinsam drehen (um die Mitte der Auswahl)',
    alignCount: 'Ausrichten ({items})',
    alignAria: 'Ausrichten',
    align: {
      left: 'Links bündig',
      'center-x': 'Horizontal zentrieren',
      right: 'Rechts bündig',
      top: 'Oben bündig',
      'center-y': 'Vertikal zentrieren',
      bottom: 'Unten bündig',
    },
    distribute: 'Verteilen',
    distributeX: 'Horizontal gleichmäßig',
    distributeY: 'Vertikal gleichmäßig',
    gapAria: 'Fester Abstand (m)',
    gapX: '↔ Abstand',
    gapY: '↕ Abstand',
    gapHint:
      '„Abstand“ reiht die Objekte mit genau diesem freien Maß aneinander, z. B. für Regalgassen.',
    alongWall: 'Entlang der Wand verschieben ({items})',
    alongAria: 'Verschiebung entlang der Wand (m)',
    towardsStart: '← Richtung Wandanfang',
    towardsEnd: 'Richtung Wandende →',
    spreadOnWall: 'Gleichmäßig auf der Wand verteilen',
    slideBlocked: 'Dort steht schon eine andere Öffnung im Weg.',
    sill: 'Brüstungshöhe ({items})',
    liftAria: 'Höhenänderung (m)',
    raise: '↑ Höher',
    lower: '↓ Tiefer',
    storeyAbove: 'In Geschoss darüber übernehmen',
    storeysAll: 'In alle oberen Geschosse',
    storeyHint: 'Türen im Erdgeschoss bleiben unten.',
    copied: 'Kopiert ✓',
    pasteHint:
      'Kopieren, dann die Zielwand anklicken (z. B. im Obergeschoss oder auf der Rückseite) und Strg+V – die Anordnung bleibt gleich.',
    placement: {
      'no-wall': 'Zum Einfügen zuerst die Zielwand anklicken.',
      'no-room': 'Auf dieser Wand ist dafür kein Platz.',
      'too-tall': 'Die Öffnungen sind höher als diese Wand.',
      overlap: 'Dort sitzen schon andere Öffnungen.',
    },
  },
  outdoorPanels: {
    points: 'Punkte ({n})',
    insertPoint: 'Punkt dahinter einfügen',
    fireLane: 'Feuerwehrzufahrten brauchen mindestens 3 m lichte Breite (DIN 14090).',
    extendRoad: 'Zum Verlängern im Zeichnen-Modus „Straße“ am Ende der Straße ansetzen.',
    carPark: 'PKW-Parkplatz',
    truckStalls: 'LKW-Stellplätze',
    perRow: 'Plätze je Reihe',
    rows: 'Reihen',
    rows1: '1 (Fahrgasse davor)',
    rows2: '2 (Fahrgasse dazwischen)',
    stallWidth: 'Stellplatzbreite',
    stallDepth: 'Stellplatztiefe',
    aisle: 'Fahrgasse',
    roofU: 'Lage quer (u)',
    roofV: 'Lage längs (v)',
    roofHint:
      'Lage bezogen auf die Hallenmitte. Im Grundriss ziehen, mit „Größe ändern“ an den Seiten aufziehen. Auf dem Flachdach frei drehbar, auf geneigten Dächern in 90°-Schritten.',
    toHall: 'Zur Halle ({name})',
  },
  zoneRoom: {
    alignToBuilding: 'Am Gebäude ausrichten',
    editVertices: 'Eckpunkte bearbeiten',
    floorColor: 'Bodenfarbe',
    roomArea: 'Raumfläche',
    walls: 'Wände',
    sharedWalls: p({
      one: 'Eine Seite nutzt die Wand des Nachbarraums bzw. des Gebäudes.',
      other: '{n} Seiten nutzen die Wand des Nachbarraums bzw. des Gebäudes.',
    }),
    recalcWalls: 'Wände neu berechnen',
  },
  measurementPanel: {
    title: 'Gespeichertes Maß',
  },
  polygon: {
    edge: 'Kante P{a}–P{b}',
    insertTitle: 'Punkt auf dieser Kante einfügen',
    add: '+ Punkt',
    remove: '− Punkt',
    deleteTitle: 'Punkt löschen',
  },
  background: {
    title: 'Hintergrundbild',
    empty: 'Lageplan oder Luftbild',
    tools: { scale: 'Maßstab', align: 'Punkt auf Punkt', move: 'Verschieben' },
    toolHints: {
      scale: 'Zwei Punkte einer bekannten Strecke im Bild anklicken',
      align: 'Bildpunkt anklicken, dann den Zielpunkt im Plan',
      move: 'Bild im Plan ziehen',
    },
    uploading: 'Lade hoch …',
    upload: 'Bild oder PDF hochladen (JPG/PNG/PDF)',
    hint: 'Z. B. ein Screenshot aus dem Geoportal, ein eingescannter Lageplan oder eine PDF-Zeichnung (bleibt beim Zoomen scharf, Weiß wird durchsichtig). Danach zwei Punkte einer bekannten Strecke anklicken, um den Maßstab festzulegen.',
    levelAria: 'Geschoss, dessen Plan bearbeitet wird',
    hintLevel:
      'Plan für das {level}, z. B. die PDF-Zeichnung des Obergeschosses. Er erscheint, solange der Plan dieses Geschoss zeigt; danach „Maßstab und Lage vom EG übernehmen“ oder neu kalibrieren.',
    onTop: 'Über der Planung anzeigen',
    onTopHint:
      'Der Plan liegt über Hallen, Büros und Räumen, um ihn nachzuzeichnen; Klicken, Zeichnen und Fangen gehen hindurch. Bei PDFs ist das Papier durchsichtig.',
    fromGround: 'Maßstab und Lage vom EG übernehmen',
    fromGroundHint:
      'Legt diesen Plan deckungsgleich über den EG-Plan (gleiche Mitte, Drehung und Maßstab) – für Pläne desselben Gebäudes auf gleich großem Blatt.',
    pdfPage: '„{name}“ hat {n} Seiten – welche soll als Hintergrund dienen?',
    pdfPageOption: 'Seite {n}',
    pdfInsert: 'Seite einfügen',
    show: 'Im Plan anzeigen',
    opacity: 'Deckkraft',
    opacityValue: 'Deckkraft {pct} %',
    size: 'Abmessungen',
    align: 'Ausrichten',
    alignInfo:
      'Maßstab: zwei Punkte einer bekannten Strecke im Bild anklicken. Punkt auf Punkt: erst einen Bildpunkt, dann den Zielpunkt im Plan. Verschieben: Bild im Plan ziehen. Danach sperren.',
    alignAria: 'Hintergrund ausrichten',
    other: 'Anderes Bild …',
    remove: 'Entfernen',
    takeOver: 'Grundstück und Hintergrund aus Projektdatei übernehmen …',
    takeOverHint:
      'Aus einer exportierten .indusketch-Datei (z. B. einer Vorlage mit gut eingezeichnetem Lageplan). Ersetzt Grundstücke und EG-Hintergrund; Gebäude und alles andere bleiben.',
    takeOverFrom: 'Aus „{name}“ übernehmen?',
    takeOverSites: p({
      one: '• {n} Grundstück – ersetzt die bisherigen Grundstücke',
      other: '• {n} Grundstücke – ersetzen die bisherigen Grundstücke',
    }),
    takeOverImage: '• das Hintergrundbild – ersetzt das bisherige im EG',
    takeOverKeeps:
      'Gebäude, Innenausbau, Objekte und Außenanlagen bleiben unverändert. Danach Grundstücke und Bild zusammen unter das Gebäude schieben; rückgängig mit Strg+Z.',
    takeOverEmpty: 'Die Datei enthält weder Grundstücke noch ein Hintergrundbild.',
    takeOverMissing:
      'Das Hintergrundbild fehlt in der Datei – bitte die Vorlage als .indusketch-Paket exportieren.',
  },
  videos: {
    title: 'Videos',
    none: 'Keine',
    count: { one: '{n} Video', other: '{n} Videos' },
    empty: 'Noch keine Videos. Beim Begehen in 3D mit „Aufnehmen“ einen Rundgang aufzeichnen.',
    play: 'Abspielen',
    download: 'Herunterladen',
    delete: 'Löschen',
    confirmDelete: 'Video „{name}“ endgültig löschen?',
    nameAria: 'Name des Videos',
    meta: '{date} · {duration} · {size}',
    by: 'von {name}',
    withSound: 'mit Ton',
    quota: '{used} von {quota} belegt',
    upload: 'Video-Datei hochladen …',
    uploadHint: 'MP4 oder WebM, höchstens {max}',
    loadFailed: 'Videos konnten nicht geladen werden: {reason}',
  },
  project: {
    title: 'Projekteinstellungen',
    project: 'Projekt',
    description: 'Beschreibung',
    grid: 'Raster & Fangen',
    snapOn: 'Fang an',
    gridSize: 'Rasterweite',
    customGrid: 'Eigene Rasterweite',
    showGrid: 'Raster anzeigen',
    snap: 'Am Raster fangen',
    collisions: 'Kollisionen prüfen',
    alignToBuilding: 'Am Gebäude ausrichten',
    alignToBuildingHint:
      'In schräg liegenden Gebäuden laufen Raster und Fang entlang der Wände; neue Räume, Zonen, Wände und Objekte stehen parallel zur Gebäudehülle.',
    display: 'Darstellung',
    displaySummary: '{unit} · Dach {roof}',
    unit: 'Maßeinheit',
    roof3d: 'Dach in 3D',
    roofAria: 'Dachdarstellung',
    roofModes: { visible: 'Sichtbar', transparent: 'Transparent', hidden: 'Aus' },
    wallsTransparent: 'Wände transparent',
    structureVisible: 'Stahlskelett der Hallen anzeigen',
    structureRoof: 'Dachtragwerk anzeigen (Binder, Pfetten, Dachverbände)',
    loads: 'Lasten (Vorbemessung)',
    loadsInfo:
      'Schnee- und Windlastzonen nach EN 1991-1-3 und EN 1991-1-4 mit dem nationalen Anhang des Landes (Deutschland: Zonenkarten z. B. beim DIBt; Polen: PN-EN mit Schneezonen 1–5 und Windzonen 1–3) sowie Geländehöhe des Grundstücks. Danach werden Rahmen, Binder, Pfetten, Wandriegel, Stützen und Unterzüge vorbemessen. Ohne Angabe gelten 0,85 kN/m² Schnee auf dem Dach und 0,65 kN/m² Winddruck.',
    loadsSummary: '{country} · Zone {zone} · {altitude} m ü. NN',
    country: 'Land (Lastnormen)',
    countries: { de: 'Deutschland', pl: 'Polen' },
    windZone: 'Windzone',
    windZoneDefault: { de: 'Standard (Windzone 2)', pl: 'Standard (Zone 1)' },
    terrain: 'Gelände',
    terrainDefault: { de: 'Standard (Binnenland)', pl: 'Standard (Kategorie III)' },
    terrains: {
      inland: 'Binnenland',
      coast: 'Küste und Inseln',
      open: 'Kategorie II – offenes Gelände',
      suburban: 'Kategorie III – Vorort, Gewerbe',
      urban: 'Kategorie IV – Stadt',
    },
    windPressure: 'Winddruck qₚ bis 10 m Höhe',
    snowZone: 'Schneelastzone',
    snowZoneNone: 'Ohne Angabe (übliche Schneelast)',
    snowZoneLabel: 'Zone {zone}',
    altitude: 'Geländehöhe (m ü. NN)',
    groundSnow: 'Schnee am Boden (sₖ)',
    roofLoad: 'Dachlast (Schnee + Eigengewicht)',
    loadValue: '{value} kN/m²',
    shortcuts: 'Tastenkürzel',
  },
  props: {
    aria: 'Eigenschaften',
    back: 'Projekteinstellungen',
    backTitle: 'Zurück zu den Projekteinstellungen',
    resizeStop: 'Größe ändern beenden (E)',
    shell: 'Hülle schützen',
    shellHint:
      'Lage, Größe und Umriss sind zusätzlich geschützt – auch gegen „Alles entsperren“. Innenausbau, Fenster, Türen und Tore bleiben bearbeitbar.',
    shellConfirm:
      'Hülle von „{name}“ wirklich freigeben? Danach lässt sie sich wieder verschieben und verändern.',
    resizeStart: 'Größe ändern (E)',
    moveStart: 'Verschieben (S)',
    resizeTitle: 'Griffe an den Enden bzw. Seiten ziehen – Taste E schaltet sie ein und aus',
    moveTitle: 'Verschiebe-Griff am Element ziehen – Taste S',
  },
  rotation: {
    by: 'Drehen um',
    angle: 'Drehung',
    ccw90: '90° gegen den Uhrzeigersinn',
    straighten: 'Gerade ausrichten (rechtwinklig zur Halle)',
    cwFine: '0,5° im Uhrzeigersinn (Pfeil runter)',
    ccwFine: '0,5° gegen den Uhrzeigersinn (Pfeil hoch)',
    cw90: '90° im Uhrzeigersinn',
    steps: '{label}: 90°- und 0,5°-Schritte',
  },
  lock: {
    label: '🔒 Sperren',
    hint: 'Gesperrt gegen Verschieben, Drehen und Ziehen. Werte hier bleiben änderbar.',
  },
  info: {
    about: 'Hinweis zu {title}',
    plain: 'Hinweis',
    show: 'Hinweis anzeigen',
  },
  outdoor: {
    drawTitle: 'Im Plan zeichnen',
    drawInfo:
      'Straßen, Parkplätze und Zäune lassen sich im Plan verschieben; Breite, Art und Punkte rechts anpassen. Wer am Ende einer Straße oder eines Zauns ansetzt, verlängert sie.',
    drawRoad: 'Straße / Weg zeichnen',
    drawParking: 'Parkfläche zeichnen',
    drawFence: 'Zaun zeichnen',
    surfacesTitle: 'Beläge und Grünflächen',
    surfacesInfo:
      'Art wählen, dann die Eckpunkte im Plan anklicken (z. B. auf dem Lageplan) oder schnell ein Rechteck aufziehen. Die Flächen erscheinen in der Flächenübersicht und im PDF unter „Außenflächen“.',
    parkingTitle: 'Parkplatz nach Anzahl',
    stalls: 'Stellplätze',
    car: 'PKW',
    truck: 'LKW',
    countAria: 'Anzahl Stellplätze',
    invalidCount: 'Bitte 1 bis 400 Stellplätze angeben.',
    insertParking: 'Parkplatz einfügen',
    fenceTitle: 'Zaun',
    fenceInfo:
      'Folgt der Außengrenze aller Grundstücke; Grenzen zwischen eigenen Flurstücken bleiben frei.',
    fenceAlongSite: 'Zaun entlang der Grundstücksgrenze',
  },
  dock: {
    title: 'LKW-Laderampe',
    info: 'Tor mit abgesenkter Zufahrt: 16 m ebene Standfläche, Rampe mit 10 %, 1,20 m tief, Überladebrücke 2 × 3 m. Mehrere Rampen stehen mit 4 m Achsabstand in einer gemeinsamen Zufahrt, Geländer nur ganz links und ganz rechts.',
    single: 'Einzelrampe',
    double: 'Doppelrampe',
    count: 'Anzahl Rampen',
    row: '{n} Rampen nebeneinander',
    place: 'Auf Hallenwand setzen',
    needsHall: 'Zuerst eine Halle anlegen – die Rampe sitzt in einer Hallenwand.',
    placeHint: 'Jetzt auf die Hallenwand tippen, an der die Rampe liegen soll.',
  },
  openings: {
    placeHint: 'Im Plan oder in 3D auf die Wand tippen – beliebig viele nacheinander.',
  },
  library: {
    search: 'Objekt suchen …',
    searchAria: 'Objekt suchen',
    placeHint:
      'Zum Absetzen in den Plan oder die 3D-Ansicht tippen. R dreht, Umschalt+Klick setzt weitere.',
    nothingFound: 'Nichts gefunden.',
    sizeRefs: 'Größenreferenzen',
    sizeRef: 'Größenreferenz {name}',
    ownModels: 'Eigene 3D-Modelle',
    uploading: 'Wird hochgeladen und geprüft …',
    addGlb: 'GLB-Modell hinzufügen',
    dropHint: 'Datei hierher ziehen oder auswählen · auch Draco/Meshopt',
    system: 'Systembibliothek',
    heightShort: 'H',
  },
  placed: {
    none: 'Noch keine Objekte platziert.',
    hidden: '{n} ausgeblendet',
    showAll: 'Alle einblenden',
    show: '{label} einblenden',
    hide: '{label} ausblenden',
    countHidden: '{n} ({hidden} aus)',
    focus: 'Auswählen und hinzoomen',
  },
  measure: {
    save: 'Als Maß speichern',
    hint: 'Im Plan von Punkt zu Punkt ziehen. Ecken, Kanten und Wände werden gefangen.',
  },
  selectOptions: {
    multi: 'Mehrfachauswahl (Antippen ergänzt)',
    multiTitle: 'Antippen fügt zur Auswahl hinzu bzw. entfernt',
    distances: 'Abstände zum gewählten Objekt',
    distancesTitle: 'Freie Abstände des gewählten Objekts zu seiner Umgebung',
  },
  /** Key names as printed in menus and the shortcut list. */
  keys: {
    paste: 'Strg+V',
    copy: 'Strg+C',
    cut: 'Strg+X',
    duplicate: 'Strg+D',
    selectAll: 'Strg+A',
    rotate: 'R',
    rotateBack: 'Umschalt+R',
    zoomSelection: 'F',
    move: 'S',
    resize: 'E',
    delete: 'Entf',
  },
  shortcuts: {
    undo: { keys: 'Strg+Z / Strg+Y', text: 'Rückgängig / Wiederholen' },
    save: { keys: 'Strg+S', text: 'Speichern' },
    clipboard: { keys: 'Strg+C / X / V', text: 'Kopieren / Ausschneiden / Einfügen' },
    duplicate: { keys: 'Strg+D', text: 'Duplizieren' },
    selectAll: { keys: 'Strg+A', text: 'Alle Objekte auswählen' },
    delete: { keys: 'Entf', text: 'Auswahl löschen' },
    nudge: { keys: 'Pfeiltasten', text: 'Auswahl um eine Rasterweite verschieben (Umschalt: ×10)' },
    rotate: {
      keys: 'R / Umschalt+R',
      text: 'Objekt um 90° drehen (auch das Objekt am Mauszeiger)',
    },
    placeMore: {
      keys: 'Umschalt+Klick beim Platzieren',
      text: 'Objekt absetzen und gleich ein weiteres setzen',
    },
    tools: {
      keys: 'V · W · S · R · M',
      text: 'Werkzeug Auswahl · Zeichnen · Schieben · Drehen · Messen',
    },
    resize: { keys: 'E', text: 'Größe ändern: Griffe am ausgewählten Element ein/aus' },
    multi: {
      keys: 'Umschalt+Klick / Umschalt+Ziehen',
      text: 'Mehrfachauswahl / Rahmenauswahl – Rechtsklick ins Leere oder Esc hebt sie auf',
    },
    zoomSelection: { keys: 'F', text: 'Auf Auswahl zoomen (2D-Plan)' },
    grid: { keys: 'G', text: 'Raster ein-/ausblenden' },
    zoom: { keys: '+ / −', text: 'Im 2D-Plan hinein- / herauszoomen' },
    pan: {
      keys: 'Leertaste + Ziehen',
      text: 'Plan oder 3D-Ansicht verschieben – in jedem Werkzeug, auch mitten beim Zeichnen',
    },
    siteDraft: {
      keys: 'Enter / Rücktaste',
      text: 'Zeichnen per Punkten (Grundstück, Fläche, Straße, Zaun): abschließen / letzten Punkt entfernen',
    },
    escape: {
      keys: 'Esc',
      text: 'Gezeichnetes Grundstück verwerfen, Größe ändern beenden, dann Auswahl, Messung und Platzieren abbrechen',
    },
  },
  drive: {
    getIn: 'Einsteigen (E)',
    getOut: 'Aussteigen (E)',
    forkUp: 'Gabel ▲ (R)',
    forkDown: 'Gabel ▼ (F)',
    drop: 'Absetzen (G)',
    view: 'Ansicht (V)',
    hint: 'W/S: Gas, Bremse, rückwärts · A/D: lenken',
    hintForks:
      'W/S: fahren · A/D: lenken · Gabel unter die Palette, dann R zum Anheben · ganz absenken oder G zum Absetzen',
  },
  vr: {
    button: 'VR',
    buttonTitle: 'Mit der VR-Brille begehen',
    suggestTitle: 'VR-Brille erkannt',
    suggestText: 'Gebäude und Grundstücke in VR erkunden.',
    suggestGame: 'Beim BG-Spiel mit der VR-Brille mitspielen.',
    projectsHint: 'Öffne ein Projekt, um Gebäude und Grundstücke in VR zu erkunden.',
    enter: 'In VR starten',
    later: 'Später',
    menuTitle: 'VR-Menü',
    help: 'Linker Stick: gehen (ganz drücken: laufen) · Rechter Stick: drehen · Y oder B: Menü',
    toStart: 'Zum Startpunkt',
    turnSnap: 'Drehen: ruckweise (30°)',
    turnSmooth: 'Drehen: fließend',
    startGame: 'BG-Spiel starten',
    endGame: 'BG-Spiel beenden',
    exit: 'VR beenden',
    close: 'Menü schließen',
  },
  context: {
    pasteHere: 'Hier einfügen',
    selectAll: 'Alle Objekte auswählen',
    selectEverything: 'Alles auswählen',
    lockEverything: 'Alles sperren',
    unlockEverything: 'Alles entsperren',
    protectShells: 'Alle Gebäudehüllen und Grundstücke schützen',
    deselect: 'Auswahl aufheben',
    rotateLeft90: 'Um 90° nach links drehen',
    straighten: 'Gerade ausrichten',
    rotateRight90: 'Um 90° nach rechts drehen',
    rotateLeftFine: 'Um 0,5° nach links drehen',
    rotateRightFine: 'Um 0,5° nach rechts drehen',
    selectAllSites: 'Alle Grundstücke auswählen',
    cut: 'Ausschneiden',
    duplicateOpening: 'Duplizieren (nächste freie Stelle)',
    unlock: 'Entsperren',
    lock: 'Sperren (gegen Verschieben)',
    resize: 'Größe ändern',
    move: 'Verschieben',
    insertVertex: 'Eckpunkt hier einfügen',
    railingEdgeOff: 'Geländer an dieser Kante entfernen',
    railingEdgeOn: 'Geländer an dieser Kante wieder anbringen',
    removeVertex: 'Nächsten Eckpunkt (P{n}) löschen',
    zoomSelection: 'Auf Auswahl zoomen',
    attachSite: 'Grundstück anfügen …',
  },
  plan: {
    aria: '2D-Grundriss',
    zoom: 'Zoom',
    zoomIn: 'Hineinzoomen (+)',
    zoomOut: 'Herauszoomen (−)',
    zoomSelection: 'Auf Auswahl zoomen (F)',
    grid: 'Raster ein-/ausblenden (G)',
    dimsToggle: 'Bemaßungen und Beschriftungen ein-/ausblenden',
    dimsMenu: 'Bemaßungen und Beschriftungen',
    dims: 'Bemaßungen',
    labels: 'Beschriftungen',
    dimItems: {
      site: 'Grundstückskanten',
      building: 'Hallenmaße',
      measurements: 'Gespeicherte Maße',
      grid: 'Achsraster und Feldmaße',
      distances: 'Abstände (Objekt)',
    },
    labelItems: {
      site: 'Grundstücke (Name, m²)',
      building: 'Hallen und Bürogebäude',
      mezzanines: 'Geschossflächen (Name, m²)',
      interior: 'Zonen und Räume',
      areas: 'Bereiche (Name, m²)',
      outdoor: 'Straßen und Stellplätze',
      objects: 'Objektnamen',
      symbols: 'Laderampen und Treppen',
    },
    tapWall: 'Bitte direkt auf eine Wand tippen, um die Öffnung einzusetzen.',
    stallsPreview: p({
      one: '{n} Stellplatz ({rows} × {perRow})',
      other: '{n} Stellplätze ({rows} × {perRow})',
    }),
    tooSmall: 'Zu klein für einen Stellplatz',
    north: 'Norden ist oben',
    closeHere: 'Hier klicken zum Schließen',
    snap: { corner: 'Ecke', midpoint: 'Mitte', edge: 'Kante', wall: 'Wand', grid: 'Raster' },
    siteTooFew: 'Ein Grundstück braucht mindestens drei Eckpunkte.',
    siteSelfIntersect: 'Die Grundstücksgrenze darf sich nicht selbst kreuzen.',
    areaTooFew: 'Eine Fläche braucht mindestens drei Eckpunkte.',
    areaSelfIntersect: 'Der Umriss darf sich nicht selbst kreuzen.',
    mezzanineNoHall: 'Eine Geschossfläche muss innerhalb einer Halle gezeichnet werden.',
    lineTooShort: 'Die Linie ist zu kurz.',
  },
  planBackground: {
    scale1: 'Ersten Punkt einer bekannten Strecke im Bild anklicken',
    scale2: 'Zweiten Punkt der Strecke anklicken',
    align1: 'Punkt im Bild anklicken (z. B. eine Grundstücksecke)',
    align2: 'Zielpunkt im Plan anklicken – rastet an Ecken ein',
    move1: 'Bild mit gedrückter Maus- bzw. Fingertaste verschieben',
    calibrate: 'Strecke im Bild derzeit {length}. Tatsächliche Länge:',
    realLength: 'Tatsächliche Länge',
    example: 'z. B. 48,5',
    apply: 'Übernehmen',
  },
  resizeLabels: {
    edges: 'Kanten {a} / {b}',
    edge: 'Kante {length} · Versatz {shift}',
    length: 'Länge {length}',
    width: 'Breite {length}',
    stalls: p({ one: '{n} Stellplatz · {length}', other: '{n} Stellplätze · {length}' }),
  },
  dashboard: {
    aria: 'Flächenübersicht',
    title: 'Flächen',
    headlineBuilt: 'Bebaut {built} · Halle frei {free}',
    headlineSite: 'Grundstück {area}',
    stillFree: 'noch frei',
    mezzanine: 'Bühne {name}',
    storeyArea: 'Geschossfläche gesamt',
    totalStoreyArea: 'Geschossflächen gesamt',
    officeMode: 'Büromodus',
    daylight: 'Tageslicht Dach',
    traffic: 'Verkehrsflächen',
    roads: 'Straßen / Wege',
    parking: 'Parkflächen',
    carStalls: 'PKW-Stellplätze',
    fenceLength: 'Zaunlänge',
    area: 'Bereich {n}',
    palletPlaces: 'Palettenplätze',
    palletPlacesLow: 'davon für 0,80-m-Paletten',
    palletRacks: p({ one: 'in {n} Palettenregal', other: 'in {n} Palettenregalen' }),
    shelfPlaces: 'Fachboden-Stellplätze',
    shelfArea: 'Fachbodenfläche',
    shelfRacks: p({ one: 'in {n} Fachbodenregal', other: 'in {n} Fachbodenregalen' }),
    outdoorZones: 'Außenflächen',
    built: 'Bebaute Fläche',
    freeSite: 'Freie Grundstücksfläche',
    coverage: 'Ausnutzung',
    more: '… und {n} weitere Hinweise',
    warnings: p({ one: '{n} Hinweis', other: '{n} Hinweise' }),
    warningsToggle: 'Hinweise ein- oder ausblenden',
  },
  status: {
    mode: 'Modus: {tool}',
    grid: 'Raster: {size}',
    snap: 'Fang: {state}',
    on: 'an',
    off: 'aus',
  },
  officeMode: {
    aria: 'Büromodus',
    title: 'Büromodus · {name}',
    exit: 'Büromodus beenden',
    slabsAria: 'Decken ein-/ausblenden',
    showSlab: 'Decke einblenden',
    hideSlab: 'Decke ausblenden',
    slabAbove: 'Decke über {level}',
  },
  scene: {
    aria: '3D-Darstellung',
    sectionTitle: 'Schnitt durch ein Geschoss: alles darüber wird ausgeblendet',
    sectionAria: 'Geschoss-Schnitt',
    allLevels: 'Alle Geschosse',
    sectionAt: 'Schnitt {level}',
    slabs: 'Decken',
    grid: 'Raster',
    shadows: 'Schatten',
    shadowsTitle: 'Weiche Schatten (kostet auf Tablets Leistung)',
    showRoof: 'Dach einblenden',
    hideRoof: 'Dach ausblenden',
    structure: 'Stahlskelett',
    structureTitle: 'Stützen und Binder der Hallen ein- oder ausblenden',
    structureRoof: 'Dachtragwerk',
    structureRoofTitle:
      'Binder, Pfetten, Dachverbände und Vouten ein- oder ausblenden; Stützen und Wandriegel bleiben stehen',
    screenshot: 'Screenshot',
    options: 'Ansicht',
    walk: 'Begehen',
    walkTitle: 'Als Person durch die Anlage laufen',
    walkStop: 'Begehen beenden',
    walkPlace: 'Klicke in die Szene, wo du starten möchtest.',
    walkHint:
      '↑ ↓: laufen · ← →: drehen · Umschalt: Rennmodus an/aus · V: Ansicht · B halten: zurückschauen · Maus ziehen: umschauen · Klick: auswählen · Objekt ziehen: verschieben · Esc: beenden',
    walkHintTouch:
      'Joystick: vor/zurück laufen, links/rechts drehen · Wischen: umschauen · Antippen: auswählen · Objekt ziehen: verschieben',
    walkJoystick: 'Laufen',
    walkRun: 'Rennen',
    walkRunTitle: 'Rennmodus ein/aus (Taste: Umschalt); nur damit wird gerannt',
    walkViews: { eye: 'Ich-Sicht', shoulder: 'Schulter', behind: 'Von hinten' },
    walkViewTitle: 'Ansicht wechseln: eigene Augen, über die Schulter, von hinten (Taste: V)',
    walkBack: 'Zurück',
    walkBackTitle: 'Gedrückt halten: nach hinten schauen (Taste: B)',
    record: 'Aufnehmen',
    recordTitle: 'Begehung als Video aufnehmen (wird beim Beenden heruntergeladen)',
    recordStop: 'Aufnahme beenden',
    recording: 'Aufnahme',
    recordSaved: 'Video gespeichert: {file}',
    walkVideoLabel: 'Begehung',
    microphone: 'Mikrofon',
    microphoneTitle:
      'Eigene Erläuterungen über das Mikrofon mit ins Video aufnehmen (gilt ab der nächsten Aufnahme)',
    micUnavailable:
      'Kein Mikrofon verfügbar oder nicht erlaubt – das Video wird ohne Ton aufgenommen.',
    videoQualityHigh: 'Hoch',
    videoQualityStandard: 'Standard',
    videoQualityTitle:
      'Videoqualität: Standard (720p, etwa 22 MB pro Minute) oder hoch (Full HD, etwa 60 MB pro Minute); gilt ab der nächsten Aufnahme',
    recordFailed:
      'Die Aufnahme ist fehlgeschlagen. Bitte erneut versuchen, notfalls in Standardqualität.',
    videoUploaded: 'Video im Projekt gespeichert: {file}',
    videoUploadFailed:
      'Das Video konnte nicht im Projekt gespeichert werden ({reason}). Die Datei wurde heruntergeladen und lässt sich unter „Videos“ später hochladen.',
    videoUploading: 'Video wird im Projekt gespeichert … {percent} %',
  },
  spaceMouse: {
    connectedAria: 'SpaceMouse verbunden: {device}',
    connect: 'SpaceMouse verbinden',
    connected: 'Verbunden: {device}',
    notConnected: 'Nicht verbunden',
    ratesTitle:
      'Wenige Signale: Gerät/Treiber. Wenige Bilder: Rechner zeichnet die 3D-Szene zu langsam.',
    rates: 'Diagnose: {reports} Signale/s · {frames} Bilder/s',
    speed: 'Geschwindigkeit {pct} %',
    invertPan: 'Verschieben umkehren',
    invertZoom: 'Zoom umkehren',
    invertRotate: 'Drehen umkehren',
    help: '2D: Kappe schieben verschiebt, drücken/ziehen zoomt. 3D: schieben verschiebt, vor/zurück zoomt, kippen und drehen dreht die Ansicht. Tasten: Menü wechselt 2D/3D, Fit zeigt alles, T/F/R wählen Oben/Vorne/Seite. Nur in Chrome und Edge.',
    busy: 'SpaceMouse ist von einem anderen Programm oder Tab belegt.',
    failed: 'Verbindung nicht möglich.',
  },
  screenshot: {
    title: 'Screenshot der 3D-Ansicht',
    hint: 'Aufgenommen wird der aktuelle Blickwinkel – ohne Werkzeugleisten, Auswahl und Griffe.',
    resolution: 'Auflösung',
    a4: 'A4 quer, 300 dpi (3508 × 2480)',
    background: 'Hintergrund',
    backgrounds: { white: 'Weiß', transparent: 'Transparent', scene: 'Wie in der Ansicht' },
    creating: 'Wird erstellt …',
    save: 'Bild speichern',
  },
  report: {
    title: 'PDF-Entwurfsbericht',
    intro:
      'Der Bericht enthält Projektdaten, Kennzahlen, den maßstäblichen Grundriss mit Legende, zwei 3D-Perspektiven sowie Flächen-, Tor- und Objektlisten. Er wird aus dem gespeicherten Stand erzeugt; offene Änderungen werden vorher gespeichert.',
    includeCosts:
      'Mengen und Kostenschätzung beilegen (Einheitspreise aus den Projekteinstellungen)',
    omitHidden: p({
      one: '{n} ausgeblendetes Objekt im Bericht weglassen',
      other: '{n} ausgeblendete Objekte im Bericht weglassen',
    }),
    steps: {
      saving: 'Speichere den aktuellen Stand …',
      rendering: 'Erzeuge 3D-Ansichten',
      generating: 'Erstelle das PDF …',
    },
    stepOf: '{i} von {n}',
    language: 'Sprache des Berichts',
    done: 'Der Bericht wurde heruntergeladen.',
    create: 'PDF erstellen',
  },
  messages: {
    commandFailed: '„{label}“ ist fehlgeschlagen. Die Planung wurde nicht verändert.',
    commandRejected: '„{label}“ nicht möglich',
    commandRejectedWhy: '„{label}“ nicht möglich: {reason}',
    saveConflict: 'Das Projekt wurde an anderer Stelle geändert. Es wurde nichts überschrieben.',
    resolveConflictFirst: 'Das Projekt wurde anderswo geändert – bitte zuerst den Konflikt lösen.',
    saveFailed: 'Speichern ist fehlgeschlagen – bitte die Verbindung prüfen.',
    noProject: 'Kein Projekt geöffnet',
    imageRead: 'Bild konnte nicht gelesen werden',
    glbOnly: 'Bitte eine .glb-Datei auswählen.',
    tooLarge: 'Datei ist zu groß (max. {mb} MB).',
    openFailed: 'Projekt kann nicht geöffnet werden: {reason}',
    loadingProject: 'Projekt wird geladen …',
    closeMessage: 'Meldung schließen',
  },
};

export type EditorMessages = typeof editor;
